import { Button, dialog } from "nq-component";
import BaseListPresenter from "../../../../base/BaseListPresenter";
import { GetCurrentManilaTimeAPICall } from "../../../../components/common/Converter";

class BiometricDevicesPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    this.limit = 50;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      objects.sort((a, b) => {
        const nameA = a?.name || "";
        const nameB = b?.name || "";

        const startsWithNumberA = /^\d/.test(nameA);
        const startsWithNumberB = /^\d/.test(nameB);

        if (startsWithNumberA && !startsWithNumberB) return -1; // numbers last
        if (!startsWithNumberA && startsWithNumberB) return 1; // letters first

        return nameA.localeCompare(nameB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });

      const dateNow = await GetCurrentManilaTimeAPICall();
      objects.forEach((object) => {
        // Use UTC timestamps
        const update = new Date(object.updated).getTime(); // Convert to UTC timestamp
        console.log("update: ", update);
        // Calculate difference in seconds
        const difference = (dateNow - update) / 1000;
        const stats = difference > 70 ? "offline" : "online";
        // console.log(`${object?.deviceName}`, update);

        // Update the object's status field
        object.stats = stats;
      });
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  // BiometricDevices.js page
  handleClickOk = () => {
    dialog.fire({
      html: (
        <div className="row p-2">
          <div className="row">
            <h4>Confirmation</h4>
            <hr />
            <p style={{ fontSize: "13px", padding: "1rem" }}>
              Are you sure you want to make this changes?
            </p>
            <hr />
          </div>

          <div className="w-100 d-flex justify-content-end gap-4 pe-3 pb-3">
            <Button
              className="btn btn-light fs-sm"
              onClick={() => dialog.close()}
              disabled={this.view.state.submit}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              onClick={() => {
                dialog.close();
                this.confirmAssign();
              }}
              progress={this.view.state.submit}
            >
              Confirm
            </Button>
          </div>
        </div>
      ),
      footer: false,
    });
  };

  async confirmAssign() {
    const { bioData } = this.view.state;
    this.view.setState({ submit: true });
    try {
      const originalSerials = this.view.props.bioData.serialNum || [];
      const currentSerials = bioData.serialNum || [];

      const addedDeviceIds = currentSerials.filter(
        (sn) => !originalSerials.includes(sn),
      );
      const removedDeviceIds = originalSerials.filter(
        (sn) => !currentSerials.includes(sn),
      );

      const user = await this.findObjectUseCase.execute("users", {
        where: { employee: { id: bioData.employee.id } },
        include: ["employee"],
        keys: [
          "username",
          "bioPass",
          "employee.Firstname",
          "employee.Middlename",
          "employee.surname",
          "employee.biometricId",
        ],
        limit: 1,
      });

      if (user.length === 0) {
        throw new Error("User not found");
      }

      const empUser = user[0];
      const name = [
        empUser?.employee?.surname,
        empUser?.employee?.Firstname,
        empUser?.employee?.Middlename,
      ]
        .filter(Boolean)
        .join(" ");
      const pri =
        empUser.username === "masteradmin@bi.com" || empUser.username === "1"
          ? "14"
          : "0";

      // Add devices
      if (addedDeviceIds.length > 0) {
        const biometric_prints = await this.findObjectUseCase.execute(
          "biometric_prints",
          {
            where: {
              $or: [
                { "data.PIN": String(empUser.employee.biometricId) },
                { "data.PIN": empUser.employee.biometricId },
              ],
            },
            keys: ["data"],
          },
        );
        for (const device of addedDeviceIds) {
          await this.upsertUseCase.execute("biometric_commands", {
            type: "DATA USER",
            device: { id: device },
            parameters: {
              PIN: empUser?.employee?.biometricId,
              Name: name,
              ...(empUser?.bioPass && { Passwd: empUser?.bioPass }),
              Pri: pri,
            },
          });

          for (const finger of biometric_prints) {
            await this.upsertUseCase.execute("biometric_commands", {
              type: "DATA UPDATE FINGERTMP",
              device: { id: device },
              parameters: {
                PIN: finger.data.PIN,
                FID: finger.data.FID,
                Size: finger.data.Size,
                Valid: finger.data.valid,
                TMP: finger.data.TMP,
              },
            });
          }
        }
      }

      // Remove devices
      if (removedDeviceIds.length > 0) {
        for (const device of removedDeviceIds) {
          await this.upsertUseCase.execute("biometric_commands", {
            type: "DATA DELETE USERINFO",
            device: { id: device },
            parameters: { PIN: empUser.employee.biometricId },
          });
        }
      }

      await this.upsertUseCase.execute("users", {
        id: bioData.id,
        ...(bioData?.isRemoved && { isRemoved: bioData.isRemoved }),
        serialNum: bioData.serialNum,
        removedSN: bioData.removedSN,
      });

      this.view.props.closeModal();
      this.view.props.refetchData();
    } catch (e) {
      console.error("Error in confirmAssign:", e);
      this.view.showError(e.message || e);
    } finally {
      this.view.setState({ submit: false });
    }
  }
}
export default BiometricDevicesPresenter;
